import { Menu, Text, Title } from "@mantine/core";
import { IconMailForward } from "@tabler/icons-react";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Content } from "shared/components/global/Content";
import { FloatingActionButton } from "shared/components/global/FloatingActionButton/FloatingActionButton";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { MessageDetailCard } from "shared/components/message/MessageDetailCard";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { usePermissionCheck } from "shared/stores/oidc";
import { arr } from "shared/utils/array";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import { MessageStatus, Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const Component = () => {
    const {
        data: { pages },
    } = useSuspenseInfiniteQuery(query);
    const messages = pages.flat();

    const canSendToGroup = usePermissionCheck(Permission.GroupMessageSendTo);
    const canSendToTopic = usePermissionCheck(Permission.TopicMessageSendTo);
    const canSend = canSendToGroup || canSendToTopic;
    const nav = useNavigate();

    const actions = useMessageActions();

    return (
        <Content>
            <Content.Heading>
                <Title order={3} size="h1">
                    Sent Messages
                </Title>
            </Content.Heading>
            {messages.length == 0 && (
                <NoMessagesFigure>
                    <Text ta="center">Looks like you haven't sent any messages yet!</Text>
                </NoMessagesFigure>
            )}

            {messages.map((message) => (
                <MessageDetailCard
                    key={message.id + message.status}
                    unread
                    actions={arr
                        .of({
                            icon: null,
                            label: "Update Status",
                            onClick: () => actions.openUpdateStatusModal(message.id),
                            disabled: message.status != MessageStatus.Published,
                        })
                        .appendIf(canSend, {
                            icon: <IconMailForward size={16} />,
                            label: "Forward",
                            onClick: () => nav({ to: "/messages/$id/fwd", params: { id: message.id } }),
                        })
                        .take()}
                    onClick={() => nav({ to: "/messages/$id", params: { id: message.id } })}
                    status={message.status}
                    title={message.subject}
                    timestamp={message.publishedAt ?? undefined}
                    severity={message.severity}
                    body={message.body}
                />
            ))}

            <LoadMoreTrigger query={query} />

            {canSend && (
                <FloatingActionButton>
                    <Menu.Item component={Link} to="/messages/compose">
                        New Message
                    </Menu.Item>
                </FloatingActionButton>
            )}
        </Content>
    );
};

const query = createOffsetBasedGqlQueryOptions({
    document: graphql(`
        query SentMessages($limit: Int!, $offset: Int!) {
            sent(limit: $limit, offset: $offset) {
                id
                subject
                body
                status
                severity
                publishedAt
                sender {
                    group {
                        id
                    }
                }
            }
        }
    `),
    queryKey: qk("messages", "sent"),
    select: ({ sent }) => sent,
});

/**
 * @public
 */
export const Route = createFileRoute("/_auth/messages/sent")({
    shouldReload: false,
    component: Component,
    loader: () => queryClient.prefetchInfiniteQuery(query),
});
